import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { getMainColor } from '@app/core/utils';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';

import { UiModule } from '@app/ui';
import { UIState } from '@app/ui/states/ui.state';
import { TranslatePipe } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { AssetUiService } from '../../services/assets-ui-service';
import {
  PostImageAssetsRequest,
  SetRawImage,
  StoreAsset,
} from '../../states/assets.actions';
import { AssetKey } from '../../states/assets.state';
import { AssetDynamicMockComponent } from '../asset-dynamic-mock/asset-dynamic-mock.component';

@Component({
  selector: 'rk-asset-display',
  templateUrl: './asset-display.component.html',
  styleUrl: './asset-display.component.scss',
  standalone: true,
  imports: [MatProgressSpinnerModule, UiModule, AssetDynamicMockComponent, TranslatePipe],
})
export class AssetDisplayComponent implements OnInit, OnChanges, OnDestroy {
  @Select(AppConfigurationState.availableOnAndroidTV)
  availableOnAndroidTV$: Observable<boolean>;
  @Select(UIState.isCreationProcess) isCreationProcess$: Observable<boolean>;
  @Select(UIState.menuWidth) menuWidth$: Observable<number>;

  @Input() imageUrl: string;
  @Input() assetKey: AssetKey;
  @Input() mockImage?: string;
  @Input() mockImageClass?: string;
  @Output() readonly crop = new EventEmitter<AssetKey>();
  @Output() readonly replace = new EventEmitter<string>();
  imageLoaded = false;
  imageError = false;
  previousImageUrl: string;
  assetMainColor: string;
  @Input() assetWidth = 200;

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly assetUiService: AssetUiService,
  ) {}

  ngOnInit() {
    this.subscriptions.add(
      this.assetUiService
        .setupAssetWidthCalculation(
          this.availableOnAndroidTV$,
          this.isCreationProcess$,
          this.menuWidth$,
        )
        .subscribe(width => {
          this.assetWidth = width;
        }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['imageUrl'] &&
      changes['imageUrl'].currentValue !== changes['imageUrl'].previousValue
    ) {
      this.imageLoaded = false;
      this.imageError = false;
      this.previousImageUrl = changes['imageUrl'].currentValue;
    }
  }

  async onImageLoad() {
    // background color logic
    try {
      this.assetMainColor = await getMainColor(this.imageUrl, 0.1);
      if (this.imageUrl === this.previousImageUrl) {
        this.imageLoaded = true;
      }
    } catch (error) {
      console.error('Error detecting main color:', error);
      if (this.imageUrl === this.previousImageUrl) {
        this.imageLoaded = true;
      }
    }
  }

  /**
   * Handles a broken image URL by falling back to the upload field.
   */
  onImageError() {
    this.imageError = true;
  }

  onRawImageSelected(key: string, selectedFile: any) {
    const file = selectedFile.file;
    const imageUrl = URL.createObjectURL(file);
    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: `Upload${key}`,
        component: 'ImageUpload',
      }),
    );
    this.store.dispatch(new LogEvent('Image uploaded'));

    if (!file) {
      return;
    }
    this.setRawImage(key, file);
    this.storeAsset(key, file, imageUrl);
    this.postImageAssets();
  }

  @Dispatch()
  postImageAssets() {
    return new PostImageAssetsRequest();
  }

  @Dispatch()
  setRawImage(key: string, image: File) {
    return new SetRawImage(key, image);
  }

  @Dispatch()
  storeAsset(key: string, file: File, url: string) {
    return new StoreAsset(key, file, url);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
